import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Typography } from '@mui/material';
import { resolvePublicUrl } from '../../utils/publicUrl';
import NavBar from '../CMS/NavBar/NavBar';
import SystemBannerFromGrid from './SystemBannerFromGrid';
import { WV_GOLD } from '../../theme';
function OneStopFullHero(props) {
    const middleHeroTitleFontFamily = '"Aptos Display", "Aptos", sans-serif';
    const { title, titleLines, titleImage, titleImageAlt, subheading, subheadingLines, description, descriptionLines, backgroundImage, nav, systemBanner, } = props;
    /* Client redesign assets — swap the hero logo + background to the "Speed to Build" images
       (see public/images/WestVirginia/onestophero). Styles/layout unchanged. */
    const STB_LOGO = '/images/WestVirginia/onestophero/herologo.png';
    const STB_BG = '/images/WestVirginia/onestophero/herobg.webp';
    const titleImgSrc = resolvePublicUrl(STB_LOGO) ?? STB_LOGO;
    const bg = resolvePublicUrl(STB_BG) ?? STB_BG;
    const renderTitleLines = () => {
        const lines = titleLines && titleLines.length ? titleLines : [title];
        return lines.map((line, index) => (_jsx(Typography, { component: "div", sx: {
                fontSize: 'clamp(1.7rem, 3vw, 3.15rem)',
                fontWeight: 600,
                color: '#0b2b4a',
                lineHeight: 1.00,
                textAlign: 'center',
                fontFamily: middleHeroTitleFontFamily,
                letterSpacing: '0.09em',
            }, children: line }, index)));
    };
    const renderSubheading = () => {
        const lines = subheadingLines && subheadingLines.length ? subheadingLines : subheading ? [subheading] : [];
        return lines.map((line, index) => (_jsx(Typography, { variant: "subtitle2", sx: {
                color: '#244c2f',
                fontWeight: 700,
                letterSpacing: '1.0em',
                textAlign: 'center',
                mt: 1,
                fontSize: { xs: '0.72rem', md: '0.8rem' },
            }, children: line }, index)));
    };
    const renderDescription = () => {
        const lines = descriptionLines && descriptionLines.length ? descriptionLines : description ? [description] : [];
        return lines.map((line, index) => (_jsx(Typography, { variant: "body2", sx: {
                color: '#0b2b4a',
                fontWeight: 700,
                textAlign: 'center',
                mt: 1,
                fontSize: { xs: '0.8rem', md: '0.84rem' },
                letterSpacing: '0.32em',
            }, children: line }, index)));
    };
    return (_jsxs(_Fragment, { children: [nav ? _jsx(NavBar, { ...nav, embedInHero: true }) : null, systemBanner ? _jsx(SystemBannerFromGrid, { ...systemBanner }) : null, _jsxs(Box, { component: "section", id: "hero", sx: { width: '100%', bgcolor: '#ffffff', position: 'relative', overflow: 'visible' }, children: [bg ? (_jsx(Box, { id: "hero-bg", "aria-hidden": true, sx: {
                            display: { xs: 'none', md: 'block' },
                            position: 'absolute',
                            top: 0,
                            bottom: 0,
                            right: 0,
                            width: { md: '50%', lg: '46%' },
                            backgroundImage: `url(${bg})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center top',
                            backgroundRepeat: 'no-repeat',
                            WebkitMaskImage: 'linear-gradient(to right, rgba(0,0,0,0) 0%, rgba(0,0,0,0.35) 20%, rgba(0,0,0,1) 36%)',
                            maskImage: 'linear-gradient(to right, rgba(0,0,0,0) 0%, rgba(0,0,0,0.35) 20%, rgba(0,0,0,1) 36%)',
                            /* Push the bridge a little toward the right edge. */
                            transform: { md: 'translateX(24px)' },
                            zIndex: 0,
                        } })) : null, _jsx(Box, { sx: {
                            width: '100%',
                            px: { xs: 2, md: 4 },
                            /* No top padding when the system banner already sits directly above — it must butt
                               right up against the banner with zero gap; otherwise keep the normal breathing room. */
                            pt: systemBanner ? 0 : { xs: 1.5, md: 1.25 },
                            pb: { xs: 1.5, md: 1.25 },
                            display: 'flex',
                            justifyContent: { xs: 'center', lg: 'flex-start' },
                            position: 'relative',
                            zIndex: 2,
                        }, children: _jsxs(Box, { sx: {
                                width: '100%',
                                minWidth: 0,
                                display: 'grid',
                                /* Narrower side columns → wider (stretched) center block. */
                                gridTemplateColumns: {
                                    xs: '140px 1fr',
                                    md: '180px 1fr',
                                    lg: 'clamp(14rem, 15vw, 17rem) minmax(0, 1fr) clamp(14rem, 15vw, 17rem)',
                                },
                                alignItems: 'center',
                                gap: { xs: 1, md: 1.5, lg: 2 },
                            }, children: [_jsx(Box, { sx: {
                                        display: 'flex',
                                        justifyContent: { xs: 'center', lg: 'flex-start' },
                                        alignItems: 'center',
                                        flexShrink: 0,
                                        gridColumn: { lg: '1' },
                                        /* Let the bigger logo overflow this cell rather than grow the hero band. */
                                        overflow: 'visible',
                                    }, children: titleImgSrc ? (_jsx(Box, { component: "img", src: titleImgSrc, alt: titleImageAlt ?? title, sx: {
                                            /* Big logo, but negative vertical margins keep its layout footprint small so the hero
                                               band stays compact — the logo just overflows (bleeds) its slot top & bottom. */
                                            // height: { xs: 160, sm: 195, md: 250, lg: 290 },
                                            //  my: { md: '-30px', lg: '-45px' },
                                            width: 'auto',
                                            maxWidth: '125%',
                                            objectFit: 'contain',
                                            /* Shift the logo toward the right (see reference start position). */
                                            transform: { md: 'translateX(48px)' },
                                        } })) : null }), _jsxs(Box, { sx: {
                                        width: '100%',
                                        justifySelf: 'center',
                                        gridColumn: { lg: '2' },
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        textAlign: 'center',
                                    }, children: [_jsxs(Typography, { component: "h1", sx: {
                                                m: 0,
                                                width: '100%',
                                                color: '#0b2b4a',
                                                fontWeight: 800,
                                                lineHeight: 1.05,
                                                letterSpacing: '-0.01em',
                                                fontSize: 'clamp(1.4rem, 2.4vw, 2.2rem)',
                                                textAlign: 'center',
                                            }, children: ["Built for", ' ', _jsx(Box, { component: "span", sx: { color: WV_GOLD, fontStyle: 'italic' }, children: "Business." }), _jsx("br", {}), "Designed for", ' ', _jsx(Box, { component: "span", sx: { color: WV_GOLD, fontStyle: 'italic' }, children: "Growth." })] }), _jsxs(Box, { sx: { alignSelf: 'center', width: 'fit-content', maxWidth: '100%' }, children: [_jsx(Box, { sx: { height: 2, bgcolor: '#0b2b4a', opacity: 0.9, my: { xs: 1, md: 1.25 }, width: '100%' } }), _jsxs(Typography, { sx: {
                                                        color: '#0b2b4a',
                                                        fontWeight: 500,
                                                        fontSize: { xs: '0.85rem', md: '1.15rem' },
                                                        lineHeight: 1.45,
                                                        textAlign: 'center',
                                                        width: '100%',
                                                    }, children: ["West Virginia's new permitting portal", _jsx(Box, { component: "br", sx: { display: { xs: 'none', md: 'block' } } }), ' ', "allows businesses to spend less time", _jsx(Box, { component: "br", sx: { display: { xs: 'none', md: 'block' } } }), ' ', "navigating regulations and more", _jsx(Box, { component: "br", sx: { display: { xs: 'none', md: 'block' } } }), ' ', "time building their future."] })] }), _jsx(Box, { sx: {
                                                mt: { xs: 1, md: 1.25 },
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                gap: { xs: 2, md: 3 },
                                                justifyContent: 'center',
                                            }, children: ['Permits', 'Licenses', 'Certifications'].map((label) => (_jsxs(Box, { component: "span", sx: {
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 0.6,
                                                    color: '#0b2b4a',
                                                    fontWeight: 800,
                                                    fontSize: { xs: '0.85rem', md: '0.92rem' },
                                                }, children: [_jsx(Box, { component: "svg", viewBox: "0 0 32 24", "aria-hidden": true, sx: { width: '1.5em', height: '1.15em', flexShrink: 0, display: 'block' }, children: _jsx("path", { d: "M0 8 H14 V3 L30 12 L14 21 V16 H0 Z", fill: WV_GOLD }) }), label] }, label))) })] }), _jsx(Box, { "aria-hidden": true, sx: {
                                        display: { xs: 'none', lg: 'block' },
                                        gridColumn: '3',
                                    } })] }) })] })] }));
}
export default OneStopFullHero;
